import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useProfile, useSubscription, useUser } from '../../stores';
import NotAuthorized from '../../pages/main/NotAuthorized';
import LoadingWindow from '../common/ui/LoadingWindow'

interface CreatorRouteProps {
  children?: React.ReactNode;
}

export const CreatorRoute: React.FC<CreatorRouteProps> = ({ children }) => {
  const { user, loading: authLoading } = useUser();
  const { profile, loading: profileLoading } = useProfile();
  const { isLoadingSubscription } = useSubscription();
  const location = useLocation();

  // Only block on isLoadingSubscription during initial load (no subscription data yet).
  // Background refreshes (e.g. from SubscriptionTab) should not unmount the page tree.
  const isInitialSubscriptionLoad = isLoadingSubscription && !profile?.subscription;
  if (authLoading || profileLoading || isInitialSubscriptionLoad) {
    return <LoadingWindow fullScreen={true} overlay={true} message="Loading Moddy AI..." />
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/" />;
  }

  // Profile not yet loaded (e.g. just signed up, profile fetch hasn't completed)
  if (!profile) {
    return <LoadingWindow fullScreen={true} overlay={true} message="Loading Moddy AI..." />
  }

  // Check subscription status from profile - redirect to subscribe page if no active access
  const subscription = profile?.subscription;
  if (subscription) {
    const hasAccess = subscription.status === 'active' || subscription.status === 'trialing';

    // Redirect if status is not active/trialing (e.g., past_due, canceled, incomplete, etc.)
    if (!hasAccess) {
      return <Navigate to="/subscribe" replace />;
    }
  }

  // If authenticated but not a creator, redirect to not authorized
  const isCreator = profile?.subscription?.access?.includes('creator');

  if (!isCreator) {
    return <NotAuthorized />
  }

  // Skip lab check if we're already on the onboarding page
  if (location.pathname === '/onboard') {
    return children ? <>{children}</> : <Outlet />;
  }

  // If creator but no labs, redirect to onboarding
  if (!profile?.labs || profile.labs.length === 0) {
    const prev = encodeURIComponent(document.referrer || '/');
    return <Navigate to={`/onboard?prev=${prev}`} replace />;
  }

  // If authenticated and is a creator with labs, show the protected content
  return children ? <>{children}</> : <Outlet />;
};
